import React, { useState, useCallback } from "react";
import { Upload, X, FileText, Download, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import Papa from "papaparse";

// Sample CSV data for download
const sampleCSV =
  "name,email\nJohn Doe,john@example.com\nJane Smith,jane@example.com";

const CSVUploader = ({ onFileProcessed, onClose }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mapping, setMapping] = useState({
    name: "",
    email: "",
  });

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length) {
      processFile(files[0]);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length) {
      processFile(files[0]);
    }
  };

  // Process the uploaded file
  const processFile = (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      toast.error("Please upload a valid CSV file");
      return;
    }

    setFile(file);
    setIsLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length) {
          toast.error("Error parsing CSV file");
          setIsLoading(false);
          return;
        }

        const csvHeaders = Object.keys(results.data[0] || {});

        // Auto-detect columns that might contain name and email
        const nameCol =
          csvHeaders.find((h) => h.toLowerCase().includes("name")) ||
          csvHeaders[0] ||
          "";

        const emailCol =
          csvHeaders.find(
            (h) =>
              h.toLowerCase().includes("email") ||
              h.toLowerCase().includes("mail")
          ) ||
          csvHeaders[1] ||
          "";

        if (!nameCol || !emailCol) {
          toast.error(
            "Could not find required columns (name and email) in the CSV"
          );
          setFile(null);
          setIsLoading(false);
          return;
        }

        setMapping({
          name: nameCol,
          email: emailCol,
        });

        // Filter and validate data
        const validData = results.data
          .map((row) => ({
            name: (row[nameCol] || "").trim(),
            email: (row[emailCol] || "").trim(),
          }))
          .filter((emp) => emp.name && emp.email);

        setPreviewData(validData.slice(0, 5)); // Show first 5 valid rows for preview
        setIsLoading(false);
      },
      error: (error) => {
        toast.error("Error parsing CSV file");
        setIsLoading(false);
      },
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select a CSV file");
      return;
    }

    // Process the file with the detected mappings
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const employees = results.data
          .map((row) => ({
            name: (row[mapping.name] || "").trim(),
            email: (row[mapping.email] || "").trim(),
          }))
          .filter((emp) => emp.name && emp.email);

        if (employees.length === 0) {
          toast.error("No valid employee data found in the CSV");
          return;
        }

        onFileProcessed(employees);
        onClose();
      },
      error: (error) => {
        toast.error("Error processing CSV file");
      },
    });
  };

  // Download sample CSV
  const downloadSampleCSV = () => {
    const blob = new Blob([sampleCSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "employee_template.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-fg-70 mb-4">
        Upload a CSV file with employee details. The file should include columns
        for name and email.
        <button
          type="button"
          onClick={downloadSampleCSV}
          className="ml-2 text-ac-02 hover:text-ac-01 inline-flex items-center text-sm"
        >
          <Download size={14} className="mr-1" /> Download Sample CSV
        </button>
      </div>

      {!file ? (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragging
              ? "border-ac-02 bg-ac-02/5"
              : "border-bd-50 hover:border-ac-02/50"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById("csv-upload").click()}
        >
          <div className="flex flex-col items-center justify-center space-y-2">
            <Upload size={24} className="text-fg-60" />
            <p className="text-sm text-fg-60">
              {isDragging
                ? "Drop the file here"
                : "Drag & drop your CSV file here, or click to browse"}
            </p>
            <p className="text-xs text-fg-70">
              Supports .csv files with name and email columns
            </p>
          </div>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-bg-50/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <FileText size={18} className="text-fg-60" />
              <span className="text-sm font-medium text-fg-50">
                {file.name}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setFile(null)}
              className="text-fg-60 hover:text-fg-50"
            >
              <X size={18} />
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ac-02"></div>
            </div>
          ) : (
            <>
              {previewData.length > 0 && (
                <div className="space-y-4">
                  <div className="text-sm font-medium text-fg-60 mb-2">
                    Found {previewData.length} valid employee(s) in the first 5
                    rows
                  </div>
                  <div className="text-sm text-fg-70">
                    Detected columns:{" "}
                    <span className="font-medium">{mapping.name}</span> (name)
                    and <span className="font-medium">{mapping.email}</span>{" "}
                    (email)
                  </div>
                  <div className="mt-2">
                    <div className="text-sm font-medium text-fg-60 mb-2">
                      Preview:
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-bd-50 text-sm">
                        <thead>
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-fg-70 uppercase tracking-wider bg-bg-50/30">
                              Name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-fg-70 uppercase tracking-wider bg-bg-50/30">
                              Email
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-bd-50">
                          {previewData.map((row, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 whitespace-nowrap text-fg-50">
                                {mapping.name ? row[mapping.name] || "" : ""}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-fg-50">
                                {mapping.email ? row[mapping.email] || "" : ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  className="py-2 px-4 border border-bd-50 rounded-md text-fg-50 hover:bg-bg-50 transition-colors"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="py-2 px-4 bg-ac-02 hover:bg-ac-01 rounded-md transition-colors shadow-sm text-white dark:text-black flex items-center"
                  onClick={handleSubmit}
                  disabled={!mapping.name || !mapping.email}
                >
                  Import {previewData.length} Employees
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVUploader;
